import {
  FeatureConfigOptionFragment,
  InputTypeEnum,
} from '@/shared/types/types.generated';

type MetaData = Record<string, unknown>;

/** Keys of the CREDENTIAL additional inputs (e.g. safety_password, master_secret_password). */
export const getCredentialInputKeys = (
  inputs?: FeatureConfigOptionFragment[] | null,
): string[] =>
  (inputs ?? [])
    .filter((input) => input.inputType === InputTypeEnum.Credential)
    .map((input) => input.value);

/**
 * A stored credential value is a plain secretId string, but the vault picker binds to
 * `{ vaultId, secretId }`. Expand each credential key so the picker can rehydrate it.
 */
export const expandCredentialMetaData = (
  metaData: MetaData | null | undefined,
  credentialKeys: string[],
): MetaData | undefined => {
  if (!metaData) {
    return undefined;
  }

  const expanded: MetaData = { ...metaData };
  credentialKeys.forEach((key) => {
    if (typeof expanded[key] === 'string') {
      expanded[key] = { secretId: expanded[key] };
    }
  });

  return expanded;
};

/**
 * Collapse each credential picker's `{ vaultId, secretId }` back to the secretId string
 * the backend stores in meta_data (dropping the UI-only vaultId).
 */
export const flattenCredentialMetaData = (
  metaData: MetaData | null | undefined,
  credentialKeys: string[],
): MetaData | undefined => {
  if (!metaData) {
    return undefined;
  }

  const flattened: MetaData = { ...metaData };
  credentialKeys.forEach((key) => {
    const value = flattened[key];
    if (value && typeof value === 'object') {
      // The clear path sets secretId to '', so use || (not ??) to collapse it to null.
      flattened[key] = (value as { secretId?: string }).secretId || null;
    }
  });

  return flattened;
};
